import React from 'react';
import { KaraokeUploadClientContext } from '../../context/KaraokeUploadClientContext';
import KaraokeUploadForm1VocalRemovalConfirmPopup from './KaraokeUploadForm1VocalRemovalConfirmPopup';
import KaraokeUploadFormView1 from './KaraokeUploadFormView1';
import KaraokeUploadFormView2 from './KaraokeUploadFormView2';
import KaraokeUploadForm2ConfirmPopup from './KaraokeUploadForm2ConfirmPopup';
import KaraokeUploadForm2VideoPopup from './KaraokeUploadForm2VideoPopup';
import KaraokeUploadForm2OnvocalPopup from './KaraokeUploadForm2OnvocalPopup';
import KaraokeUploadForm2OffvocalPopup from './KaraokeUploadForm2OffvocalPopup';
import KaraokeUploadForm2LyricsPopup from './KaraokeUploadForm2LyricsPopup';
import { FieldValidationErrorsType, SuperagentProgressParams } from '../../../common/form/FrekulForm';
import useCustomForm, { validateField } from '../../../common/hooks/CustomForm';

// 地のフォーム
export default function KaraokeUploadForm({
  uploadHash,
  modificationFeeAmount,
  remakeFeeAmount,
} : {
  uploadHash: string,
  modificationFeeAmount: number,
  remakeFeeAmount: number,
}) {
  type ViewName = 'view1'|'view2';
  type PopupName = 'VocalRemovalConfirm'|'Confirm'|'UploadVideo'|'UploadOnvocal'|'UploadOffvocal'|'UploadLyrics';
  type AjaxName = 'validateFirst'|'submit'|'validateLyrics';

  const [view, setView] = React.useState<ViewName>('view1');
  const [popup, setPopup] = React.useState<PopupName|null>(null);
  const [ajax, setAjax] = React.useState<AjaxName|null>(null);
  const [progressInfo, setProgressInfo] = React.useState<SuperagentProgressParams|null>(null);

  type FileFieldName = 'song_vocal_off'|'song_vocal_on'|'movie';
  type FileValuesType = { [name in FileFieldName]: FileList|null };
  type FileErrorsType = { [name in FileFieldName]?: FieldValidationErrorsType };
  const [fileValues, fileErrors, setFileValues, setFileErrors] = useCustomForm<FileValuesType, FileErrorsType, { name: FileFieldName }>({
    defaultValue: {
      song_vocal_off: null,
      song_vocal_on: null,
      movie: null,
    },
    validate(values, options, fileErrors): FileErrorsType {
      const name = options.name;
      const value = values[name];
      const error = validateField([
        ['movie', 'type', 'mp4(AVC), mov, wmvいずれかの形式を指定してください。', (value: FileList|null) => value === null || /\.(mov|mp4|wmv)$/i.test(value[0].name)],
        ['movie', 'size', '1.3GB以内のファイルを指定してください。', (value: FileList|null) => value === null || value[0].size <= 1024 * 1024 * 1024],
        ['song_vocal_on', 'type', 'wav または mp3を指定してください。', (value: FileList|null) => value === null || /\.(wav|mp3)$/i.test(value[0].name)],
        ['song_vocal_on', 'size', '150MB以内のファイルを指定してください。', (value: FileList|null) => value === null || value[0].size <= 150 * 1024 * 1024],
        ['song_vocal_off', 'type', 'wav または mp3を指定してください。', (value: FileList|null) => value === null || /\.(wav|mp3)$/i.test(value[0].name)],
        ['song_vocal_off', 'size', '150MB以内のファイルを指定してください。', (value: FileList|null) => value === null || value[0].size <= 150 * 1024 * 1024],
      ], name, value);
      return { ...fileErrors, [name]: error };
    },
  });

  type FieldName = 'background_order'|'offvocal_order'|'lyrics'|'promotion';
  type ValuesType = { [name in FieldName]: string };
  type ErrorsType = { [name in FieldName]?: FieldValidationErrorsType };
  const [values, errors, setValues, setErrors] = useCustomForm<ValuesType, ErrorsType, { name: FieldName }>({
    defaultValue: {
      background_order: '',
      offvocal_order: '',
      lyrics: '',
      promotion: '',
    },
  });

  const {
    validateFirstKaraokeUpload,
    executeKaraokeUpload,
    validateLyrics,
  } = React.useContext(KaraokeUploadClientContext);

  const validateAllBeforeSubmit = (values: ValuesType & FileValuesType): ErrorsType & FileErrorsType => {
    const errors = {} as ErrorsType & FileErrorsType;
    const fileUploaded = (files: FileList|null) => (files !== null && files.length > 0);
    if (values.background_order === 'file') {
      if (!fileUploaded(values.movie)) {
        errors.movie = {};
        errors.movie['notEmpty'] = '動画ファイルをアップロードしてください。';
      }
    }
    if (values.background_order === 'auto') {
      if (!fileUploaded(values.song_vocal_on)) {
        errors.song_vocal_on = {};
        errors.song_vocal_on['notEmpty'] = '歌あり音源をアップロードしてください。';
      }
    }
    if (values.offvocal_order !== 'vocal_removal') {
      if (!fileUploaded(values.song_vocal_off)) {
        errors.song_vocal_off = {};
        errors.song_vocal_off['notEmpty'] = '歌なし音源をアップロードしてください。';
      }
    }
    if (values.lyrics === '') {
      errors.lyrics = {};
      errors.lyrics['notEmpty'] = '歌詞を入力してください。';
    }
    return errors;
  };

  const handleConfirm = () => {
    if (popup !== null) return;
    const newErrors = validateAllBeforeSubmit({ ...values, ...fileValues });
    setAjax(null);
    setErrors(newErrors);
    setFileErrors(newErrors);
    if (Object.keys(newErrors).length === 0) {
      setPopup('Confirm');
    }
  };

  React.useEffect(() => {
    if (ajax === 'validateFirst') {
      validateFirstKaraokeUpload(uploadHash, { ...values, ...fileValues }).then((responseData) => {
        setAjax(null);
        setErrors(responseData.validationErrors || {});
        setFileErrors(responseData.validationErrors || {});
        if (responseData.result) {
          setView('view2');
        }
      });
    } else if (ajax === 'submit') {
      const handleProgressInfo = (progressInfo: SuperagentProgressParams) => {
        setAjax('submit');
        setProgressInfo(progressInfo);
      }
      // eslint-disable-next-line max-len
      executeKaraokeUpload(uploadHash, { ...values, ...fileValues }, handleProgressInfo).then((responseData) => {
        setAjax(null);
        setErrors(responseData.validationErrors || {});
        setFileErrors(responseData.validationErrors || {});
        if (responseData.result) {
          document.location.href = `/karaoke_upload/completed/${uploadHash}`;
        }
      });
    } else if (ajax === 'validateLyrics') {
      validateLyrics(values.lyrics).then((responseData) => {
        setAjax(null);
        if (responseData.result) {
          setPopup(null);
        } else {
          setErrors({ ...errors, lyrics: { error: responseData.msg }})
        }
      });
    }
  }, [ajax]);

  return (
    <>
      {view === 'view1' && (
        <KaraokeUploadFormView1
          getValue={(name) => values[name]}
          setValue={(name, value) => setValues({ ...values, [name]: value }, { name })}
          getErrors={(name) => errors?.[name] || {}}
          onNext={() => {
            if (values.offvocal_order === 'vocal_removal') {
              setPopup('VocalRemovalConfirm');
            } else {
              setAjax('validateFirst');
            }
          }}
        />
      )}
      {view === 'view2' && (
        <KaraokeUploadFormView2
          onUploadVideo={() => setPopup('UploadVideo')}
          onUploadOffvocalAudio={() => setPopup('UploadOffvocal')}
          onUploadOnvocalAudio={() => setPopup('UploadOnvocal')}
          onUploadLyrics={() => setPopup('UploadLyrics')}
          getValue={(name) => values[name]}
          getFiles={(name) => fileValues[name]}
          getErrors={(name) => ({ ...errors, ...fileErrors }?.[name] || {})}
          onBack={() => {
            setFileValues({ ...fileValues, movie: null, song_vocal_off: null, song_vocal_on: null }, { name: 'movie' });
            setView('view1');
          }}
          onNext={() => handleConfirm()}
        />
      )}
      {popup === 'VocalRemovalConfirm' && (
        <KaraokeUploadForm1VocalRemovalConfirmPopup
          onDone={() => {
            setPopup(null);
            setAjax('validateFirst');
          }}
          onCancel={() => setPopup(null)}
        />
      )}
      {popup === 'Confirm' && (
        <KaraokeUploadForm2ConfirmPopup
          getValue={(name) => values[name]}
          getFiles={(name) => fileValues[name]}
          getErrors={(name) => ({ ...errors, ...fileErrors }?.[name] || {})}
          submitting={ajax === 'submit'}
          progressInfo={progressInfo}
          remakeFeeAmount={remakeFeeAmount}
          modificationFeeAmount={modificationFeeAmount}
          onCancel={() => setPopup(null)}
          onSubmit={() => setAjax('submit')}
        />
      )}
      {popup === 'UploadVideo' && (
        <KaraokeUploadForm2VideoPopup
          getFiles={(name) => fileValues[name]}
          setFiles={(name, value) => setFileValues({ ...fileValues, [name]: value }, { name })}
          getErrors={(name) => fileErrors?.[name] || {}}
          onClose={() => setPopup(null)}
        />
      )}
      {popup === 'UploadOnvocal' && (
        <KaraokeUploadForm2OnvocalPopup
          getFiles={(name) => fileValues[name]}
          setFiles={(name, value) => setFileValues({ ...fileValues, [name]: value }, { name })}
          getErrors={(name) => fileErrors?.[name] || {}}
          onClose={() => setPopup(null)}
        />
      )}
      {popup === 'UploadOffvocal' && (
        <KaraokeUploadForm2OffvocalPopup
          getFiles={(name) => fileValues[name]}
          setFiles={(name, value) => setFileValues({ ...fileValues, [name]: value }, { name })}
          getErrors={(name) => fileErrors?.[name] || {}}
          onClose={() => setPopup(null)}
        />
      )}
      {popup === 'UploadLyrics' && (
        <KaraokeUploadForm2LyricsPopup
          getValue={(name) => values[name]}
          setValue={(name, value) => setValues({ ...values, [name]: value }, { name })}
          getErrors={(name) => errors?.[name] || {}}
          onClose={() => setAjax('validateLyrics')}
        />
      )}
    </>
  );
}
